import GeoPosition18 from '@sats-group/icons/18/geoposition';
import Search18 from '@sats-group/icons/18/search';
import Text from '@sats-group/fresh-fitness-ui-lib/react/text';
import React, { useCallback, useState } from 'react';

import interpolate from 'root/shared/interpolate';
import { replaceQueryParameters } from 'root/shared/replace-query-parameters';
import {
  type Coordinates,
  findNearestLocations,
} from 'root/shared/nearest-locations';

import ClubLink from 'root/client/components/club-link/club-link';
import type { ClubLink as ClubLinkProps } from 'root/client/components/club-link/club-link.types';

import type { LocationSearch as Props } from './location-search.types';

// NOTE setting a maximum distance to avoid getting clubs 100km away from
// your search
const maximumDistanceKm = 10;
const nearestMeMaximumResults = 5;

type GeocodingResponse = {
  features?: {
    geometry?: {
      coordinates?: unknown;
    };
  }[];
};

type SearchOrigin = {
  coordinates: Coordinates;
  description?: string;
  exactSearchTerm?: string;
  label: string;
  maximumResults?: number;
};

type SearchResults = {
  clubs: ClubLinkProps[];
  description?: string;
  title: string;
};

const normalizeSearchTerm = (value: string) =>
  value.trim().toLocaleLowerCase('nb-NO');

const coordinatesFromResponse = (
  response: GeocodingResponse,
): Coordinates | undefined => {
  const coordinates = response.features?.[0]?.geometry?.coordinates;

  return Array.isArray(coordinates) &&
    typeof coordinates[0] === 'number' &&
    typeof coordinates[1] === 'number'
    ? [coordinates[0], coordinates[1]]
    : undefined;
};

const LocationSearch: React.FC<Props> = ({
  basedOnPosition,
  clubs,
  distanceTemplate,
  emptyState,
  geocodingUrl,
  nearestMeLabel,
  nearestResultsTemplate,
  nearestYou,
  placeholder,
  searchButtonLabel,
}) => {
  const [hasNoResults, setHasNoResults] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults>();

  const updateResults = useCallback(
    ({
      coordinates,
      description,
      exactSearchTerm,
      label,
      maximumResults,
    }: SearchOrigin) => {
      const normalizedSearchTerm = exactSearchTerm
        ? normalizeSearchTerm(exactSearchTerm)
        : undefined;
      const exactMatches = normalizedSearchTerm
        ? clubs.filter(
            club =>
              normalizeSearchTerm(club.city) === normalizedSearchTerm ||
              normalizeSearchTerm(club.postalCode) === normalizedSearchTerm,
          )
        : [];
      const nearestClubs = findNearestLocations({
        locations: exactMatches.length ? exactMatches : clubs,
        origin: coordinates,
      }).filter(club => club.distance <= maximumDistanceKm);
      const visibleClubs = maximumResults
        ? nearestClubs.slice(0, maximumResults)
        : nearestClubs;
      const results = visibleClubs.map(club => ({
        distance: interpolate(distanceTemplate, {
          distance: club.distance.toFixed(1),
        }),
        href: club.href,
        name: club.name,
        openingHours: club.openingHours,
        size: club.size,
        usp: club.usp,
      }));

      setHasNoResults(!results.length);
      setSearchResults({
        clubs: results,
        description,
        title: interpolate(nearestResultsTemplate, { location: label }),
      });
    },
    [clubs, distanceTemplate, nearestResultsTemplate],
  );

  const search = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    const searchTerm = query.trim();

    if (searchTerm.length < 2 || isSearching) {
      setHasNoResults(false);
      setSearchResults(undefined);
      return;
    }

    setIsSearching(true);
    const url = replaceQueryParameters(geocodingUrl, { q: searchTerm });

    try {
      const response = await fetch(url);
      const coordinates = response.ok
        ? coordinatesFromResponse((await response.json()) as GeocodingResponse)
        : undefined;

      if (coordinates) {
        updateResults({
          coordinates,
          exactSearchTerm: searchTerm,
          label: searchTerm,
        });
      } else {
        setHasNoResults(true);
        setSearchResults(undefined);
      }
    } catch {
      setHasNoResults(true);
      setSearchResults(undefined);
    } finally {
      setIsSearching(false);
    }
  };

  const findNearestMe = () => {
    if (!navigator.geolocation) {
      setHasNoResults(true);
      setSearchResults(undefined);
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      position => {
        setIsLocating(false);
        updateResults({
          coordinates: [position.coords.longitude, position.coords.latitude],
          description: basedOnPosition,
          label: nearestYou,
          maximumResults: nearestMeMaximumResults,
        });
      },
      () => {
        setIsLocating(false);
        setHasNoResults(true);
        setSearchResults(undefined);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="location-search">
      <form className="location-search__field" onSubmit={search}>
        <span aria-hidden className="location-search__search-icon">
          <Search18 />
        </span>
        <input
          aria-label={placeholder}
          className="location-search__input"
          enterKeyHint="search"
          onChange={event => setQuery(event.target.value)}
          placeholder={placeholder}
          type="search"
          value={query}
        />
        <button
          aria-busy={isSearching}
          className="location-search__submit"
          disabled={isSearching || isLocating}
          type="submit"
        >
          {searchButtonLabel}
        </button>
        <button
          aria-label={nearestMeLabel}
          aria-busy={isLocating}
          className="location-search__nearest"
          disabled={isLocating || isSearching}
          onClick={findNearestMe}
          type="button"
        >
          <GeoPosition18 />
        </button>
      </form>
      {searchResults?.clubs.length ? (
        <section className="location-search__results">
          <div className="location-search__results-header">
            <Text size={Text.sizes.small} theme={Text.themes.emphasis}>
              {searchResults.title}
            </Text>
            {searchResults.description ? (
              <Text size={Text.sizes.small}>{searchResults.description}</Text>
            ) : null}
          </div>
          <div className="location-search__clubs">
            {searchResults.clubs.map(club => (
              <ClubLink {...club} key={club.href} />
            ))}
          </div>
        </section>
      ) : null}
      {hasNoResults ? (
        <Text className="location-search__empty" role="status">
          {emptyState}
        </Text>
      ) : null}
    </div>
  );
};

export default LocationSearch;
